// src/hooks/useReviews.js

import { useState, useEffect, useCallback } from 'react';
import reviewsApi from '../api/reviewsApi';

export const useReviews = (equipmentId, options = {}) => {
  const [reviews, setReviews] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState(null);
  
  const fetchReviews = useCallback(async () => {
    if (!equipmentId) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const data = await reviewsApi.getReviews(equipmentId, options);
      
      setReviews(data.reviews || []);
      setSummary(data.summary || null);
      setPagination(data.pagination || null);
    } catch (err) {
      console.error('Error fetching reviews:', err);
      setError(err.message);
      setReviews([]);
    } finally {
      setLoading(false);
    }
  }, [equipmentId, JSON.stringify(options)]);
  
  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);
  
  const submitReview = useCallback(async (reviewData) => {
    try {
      const result = await reviewsApi.createReview({
        ...reviewData,
        equipmentId
      });
      fetchReviews(); // Refresh list
      return result;
    } catch (err) {
      throw err;
    }
  }, [equipmentId, fetchReviews]);
  
  const updateReview = useCallback(async (reviewId, reviewData) => {
    try {
      const result = await reviewsApi.updateReview(reviewId, reviewData);
      fetchReviews(); // Refresh list
      return result;
    } catch (err) {
      throw err;
    }
  }, [fetchReviews]);
  
  const deleteReview = useCallback(async (reviewId) => {
    try {
      await reviewsApi.deleteReview(reviewId);
      fetchReviews(); // Refresh list
    } catch (err) {
      throw err;
    }
  }, [fetchReviews]);
  
  const voteOnReview = useCallback(async (reviewId, vote) => {
    try {
      await reviewsApi.voteReview(reviewId, vote);
      fetchReviews(); // Refresh to get updated counts
    } catch (err) {
      throw err;
    }
  }, [fetchReviews]);
  
  return {
    reviews,
    summary,
    pagination,
    loading,
    error,
    refresh: fetchReviews,
    submitReview,
    updateReview,
    deleteReview,
    voteOnReview
  };
};

export default useReviews;

